// The order header for a declined Website order (2026-10-19;
// server/src/ai/declined-order.js). Shown in place of a TRQ order when the
// latest suggestion matched a declined order: the order is read live from
// GET /api/conversations/:id/declined-order (only its poNumber is stored),
// with a red internal-only notice on top (never the fraud screen's name,
// 2026-10-22). Only for an email contact with no
// TRQ order; renders nothing otherwise.
import { useEffect, useState } from 'react'
import { Alert, Box, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { fetchDeclinedOrder } from './api.js'

const money = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? '' : `$${Number(v).toFixed(2)}`)
// The order date as MM/DD/YYYY when it is an ISO date, else as given.
const day = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? ''))
  return m ? `${m[2]}/${m[3]}/${m[1]}` : String(v ?? '')
}
// The internal notice (2026-10-22): the payment could not be processed, in
// internal wording, never the fraud screen's name.
export const DECLINED_NOTICE = 'Declined order: payment could not be processed (internal). It was never placed. Never tell the customer why it was declined.'
const lines = (b) => [b?.name, b?.address1, b?.address2, [b?.city, [b?.region, b?.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', '), b?.country, b?.phone].filter(Boolean)

function AddressBlock({ label, block, testId }) {
  const rows = lines(block)
  if (!rows.length) return null
  return (
    <Box data-testid={testId} sx={{ minWidth: 180 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{label}</Typography>
      {rows.map((r, i) => <Typography key={i} variant="caption" sx={{ display: 'block', lineHeight: 1.4, fontSize: 13, color: 'text.secondary' }}>{r}</Typography>)}
    </Box>
  )
}

export default function DeclinedOrderHeader({ conversation, refreshKey = null }) {
  const [data, setData] = useState(null)
  const applies = Boolean(conversation?.id) && conversation?.channel === 'm365_email' && !conversation?.erpOrderId
  useEffect(() => {
    let live = true
    setData(null)
    if (!applies) return undefined
    fetchDeclinedOrder(conversation.id)
      .then((r) => { if (live) setData(r ?? null) })
      .catch(() => { if (live) setData(null) })
    return () => { live = false }
  }, [applies, conversation?.id, refreshKey])

  const o = data?.declinedOrder
  if (!applies || !o) return null
  const card = o.card ?? {}
  return (
    <Box data-testid="declined-order-header" sx={{ px: 1, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
      <Alert severity="error" variant="filled" sx={{ mb: 0.75, py: 0 }}>{DECLINED_NOTICE}</Alert>
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontSize: 13 }}><Box component="span" sx={{ fontWeight: 700 }}>PO #: </Box>{o.poNumber}</Typography>
        {o.orderDate && <Typography variant="caption" data-testid="declined-order-date" sx={{ fontSize: 13 }}><Box component="span" sx={{ fontWeight: 700 }}>Date: </Box>{day(o.orderDate)}</Typography>}
        {o.paymentId && <Typography variant="caption" sx={{ fontSize: 13 }}><Box component="span" sx={{ fontWeight: 700 }}>Payment: </Box>{o.paymentId}</Typography>}
        {o.shippingCost != null && <Typography variant="caption" sx={{ fontSize: 13 }}><Box component="span" sx={{ fontWeight: 700 }}>Shipping: </Box>{money(o.shippingCost)}</Typography>}
        {o.orderTax != null && <Typography variant="caption" sx={{ fontSize: 13 }}><Box component="span" sx={{ fontWeight: 700 }}>Tax: </Box>{money(o.orderTax)}</Typography>}
        {o.orderTotal != null && <Typography variant="caption" sx={{ fontSize: 13 }}><Box component="span" sx={{ fontWeight: 700 }}>Total: </Box>{money(o.orderTotal)}</Typography>}
        {(card.last4 || card.expiration) && (
          <Typography variant="caption" data-testid="declined-order-card" sx={{ fontSize: 13, color: 'text.secondary' }}>
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>Card: </Box>
            {[card.last4 ? `ending ${card.last4}` : null, card.expiration ? `exp ${card.expiration}` : null].filter(Boolean).join(', ')}
          </Typography>
        )}
        {o.emailAddress && <Typography variant="caption" sx={{ fontSize: 13, color: 'text.secondary' }}>{o.emailAddress}</Typography>}
      </Stack>
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
        <AddressBlock label="Billing" block={o.billing} testId="declined-order-billing" />
        <AddressBlock label="Shipping" block={o.shipping} testId="declined-order-shipping" />
      </Stack>
      {(o.items ?? []).length > 0 && (
        <Table size="small" data-testid="declined-order-items" sx={{ '& td, & th': { fontSize: 12, py: 0.25, px: 0.75 } }}>
          <TableHead>
            <TableRow><TableCell>SKU</TableCell><TableCell>Description</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Unit price</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {o.items.map((i, idx) => (
              <TableRow key={`${i.sku}-${idx}`}>
                <TableCell>{i.url ? <Link href={i.url} target="_blank" rel="noopener noreferrer">{i.sku}</Link> : i.sku}</TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{i.description}</TableCell>
                <TableCell align="right">{i.quantity}</TableCell>
                <TableCell align="right">{money(i.unitPrice)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  )
}
